import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import '../webapp.css';
import { Navbar2, AlertStack } from '../functions/ui';  

const Onboard = () => {
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");
  const [staffSize, setStaffSize] = useState("");
  const [staffSizeError, setStaffSizeError] = useState("");
  const [funnel, setFunnel] = useState("");
  const [funnelError, setFunnelError] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [companyTypeError, setCompanyTypeError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [companyCode, setCompanyCode] = useState("");
  const [alerts, setAlerts] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanyCode();
  }, []);

  useEffect(() => {
  }, [phone, postalCode, staffSize, funnel, companyType]);

  const fetchCompanyCode = async () => {
    const userUID = getAuth().currentUser?.uid;
    if (!userUID) {
      console.error("Unable to retrieve user information. Please log in again.");
      setIsLoading(false);
      return;
    }

    const db = getFirestore();
    const docRef = doc(db, "companyUsers", userUID);
    const docSnap = await getDoc(docRef);

    setIsLoading(false);

    if (docSnap.exists()) {
      setCompanyCode(docSnap.data().companycode);
    } else {
      console.error("Company code not found");
    }
  };

  const validatePhone = (number) => {
    return true;
  };
  
  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);

    let valid = true;
    if (!phone) {
      setPhoneError("Phone is required");
      valid = false;
    }
    if (!postalCode) {
      setPostalCodeError("Postal code is required");
      valid = false;
    }
    if (!staffSize) {
      setStaffSizeError("Staff size is required");
      valid = false;
    }
    if (!funnel) {
      setFunnelError("Funnel source is required");
      valid = false;
    }
    if (!companyType) {
      setCompanyTypeError("Company type is required");
      valid = false;
    }

    if (!valid) {
      setLoading(false);
      return;
    }

    const auth = getAuth();
    const userUID = auth.currentUser?.uid;

    if (!userUID) {
      setLoading(false);
      return;
    }

    const db = getFirestore();
    const userRef = doc(db, "companyUsers", userUID);

    try {
      await setDoc(userRef, {
        phone,
        postalCode,
        staffSize,
        funnel,
        companyType,
        companyCode,
        onboarded: true 
      }, { merge: true }); // Ensures data is merged, not overwritten

      setLoading(false);
      navigate("/dashboard"); 
    } catch (error) {
      setLoading(false);
      setAlerts(prev => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          message: error.message || "An unexpected error occurred. Please try again",
          type: 'error',
        },
      ]);
    }
  };

  // Handling input changes and clearing the errors on change
  const handleInputChange = (setter, validator, errorSetter) => (event) => {
    const value = event.target.value;
    setter(value);
    errorSetter(""); // Clear the error when user changes the input
  };

  return (
    <>
      <Navbar2 /> 
      <div className="onboard-container">
        <div className="onboard-header white-container">  
          <h2>Complete Your Profile</h2>
          <p>Please complete your onboarding on Kierep!</p>
          <div style={{ textAlign: 'left', marginTop: '20px' }}>
            {isLoading ? (
              <div className="progress-view">Loading...</div>
            ) : (
              <>
                <p style={{ fontSize: '12px', color: '#555' }}>
                  Your Company Code is: <span style={{ fontWeight: 'bold' }}>{companyCode}</span>. Please share it with your staff
                </p>
                <p style={{ fontSize: '12px', color: '#555' }}>
                  to be used for signup on the staff app downloaded from the App Store.
                </p>
              </>
            )}
          </div>
        </div>
        <div className="onboard-form">
          {alerts.length > 0 && (
            <AlertStack
              alerts={alerts}
              onAlertClose={id => setAlerts(alerts => alerts.filter(a => a.id !== id))}
            />
          )}
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <form onSubmit={submit}>
              <div>
                <label>Phone</label>
                <input 
                  type="text" 
                  value={phone} 
                  onChange={handleInputChange(setPhone, validatePhone, setPhoneError)} 
                  onFocus={() => setPhoneError('')} // Clear error on focus
                />
                {phoneError && <p className="error-message">{phoneError}</p>}
              </div>


              <div>
                <label>Postal Code</label>
                <input 
                  type="text" 
                  value={postalCode} 
                  onChange={handleInputChange(setPostalCode, null, setPostalCodeError)} 
                  onFocus={() => setPostalCodeError('')} // Clear error on focus
                />
                {postalCodeError && <p className="error-message">{postalCodeError}</p>}
              </div>

              <div>
                <label>Care Staff Size</label>
                <select 
                  value={staffSize} 
                  onChange={handleInputChange(setStaffSize, null, setStaffSizeError)}
                  onFocus={() => setStaffSizeError('')} // Clear error on focus
                >
                  <option value="">Select an option</option>
                  <option value="10-30">10-30</option>
                  <option value="31-60">31-60</option>
                  <option value="61-80">61-80</option>
                  <option value="81-100">81-100</option>
                  <option value="101-120">101-120</option>
                  <option value="121 and above">121 and above</option>
                </select>
                {staffSizeError && <p className="error-message">{staffSizeError}</p>}
              </div>

              <div>
                <label>How did you hear about us</label>
                <select 
                  value={funnel} 
                  onChange={handleInputChange(setFunnel, null, setFunnelError)}
                  onFocus={() => setFunnelError('')} // Clear error on focus
                >
                  <option value="">Select an option</option>
                  <option value="Company Agent">Company Agent</option>
                  <option value="Email">Email</option>
                  <option value="Poster/flyer">Poster/flyer</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Referral">Referral</option>
                </select>
                {funnelError && <p className="error-message">{funnelError}</p>}
              </div>

              <div>
                <label>Company Type</label>
                <select 
                  value={companyType} 
                  onChange={handleInputChange(setCompanyType, null, setCompanyTypeError)}
                  onFocus={() => setCompanyTypeError('')} // Clear error on focus
                >
                  <option value="">Select an option</option>
                  <option value="Homecare Company">Homecare Company</option>
                  <option value="Child and Youth Care Company">Child and Youth Care Company</option>
                  <option value="Developmental Support Company">Developmental Support Company</option>
                </select>
                {companyTypeError && <p className="error-message">{companyTypeError}</p>}
              </div>
              <button type="submit" className="full-button" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default Onboard;
